import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";

// Layouts
import AppLayout from "@/layouts/AppLayout";
import PlatformLayout from "@/layouts/PlatformLayout";

// Pages
import { TenantLogin, PlatformLogin } from "@/pages/Login";
import POSScreen from "@/pages/POSScreen";
import ProductsPage from "@/pages/ProductsPage";
import ShiftsPage from "@/pages/ShiftsPage";
import SalesPage from "@/pages/SalesPage";
import ReportsPage from "@/pages/ReportsPage";
import SettingsPage from "@/pages/SettingsPage";
import PlatformDashboard from "@/pages/platform/Dashboard";
import TenantsPage from "@/pages/platform/TenantsPage";
import TenantDetailPage from "@/pages/platform/TenantDetailPage";

// Protected Route Component
const ProtectedRoute = ({ children, requirePlatformAdmin = false }) => {
  const { isAuthenticated, isPlatformAdmin } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to={requirePlatformAdmin ? "/platform/login" : "/app/login"} replace />;
  }
  
  if (requirePlatformAdmin && !isPlatformAdmin) {
    return <Navigate to="/platform/login" replace />;
  }
  
  if (!requirePlatformAdmin && isPlatformAdmin) {
    return <Navigate to="/platform/dashboard" replace />;
  }
  
  return children;
};

// App with Auth Check
const AppRoutes = () => {
  const { isAuthenticated, isPlatformAdmin, currentBranchId } = useAuth();

  return (
    <Routes>
      {/* Root redirect */}
      <Route 
        path="/" 
        element={
          isAuthenticated 
            ? isPlatformAdmin 
              ? <Navigate to="/platform/dashboard" replace />
              : <Navigate to="/app/pos" replace />
            : <Navigate to="/app/login" replace />
        } 
      />

      {/* Tenant Auth */}
      <Route path="/app/login" element={<TenantLogin />} />
      
      {/* Platform Auth */}
      <Route path="/platform/login" element={<PlatformLogin />} />

      {/* Tenant App Routes */}
      <Route 
        path="/app" 
        element={
          <ProtectedRoute>
            <CartProvider>
              <AppLayout />
            </CartProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/app/pos" replace />} />
        <Route path="pos" element={<POSScreen />} />
        <Route path="shifts" element={<ShiftsPage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="sales" element={<SalesPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Platform Admin Routes */}
      <Route 
        path="/platform" 
        element={
          <ProtectedRoute requirePlatformAdmin>
            <PlatformLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/platform/dashboard" replace />} />
        <Route path="dashboard" element={<PlatformDashboard />} />
        <Route path="tenants" element={<TenantsPage />} />
        <Route path="tenants/:tenantId" element={<TenantDetailPage />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
